/**
 * Template Builder - Media Step
 * 
 * Add cover image and media for the template
 */

import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Pressable, Image, Platform, Alert } from 'react-native';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import { InputField } from '@/components/ui/InputField';
import { Button } from '@/components/ui/Button';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as Linking from 'expo-linking';

type TemplateBuilderMediaStepProps = {
  draft: any;
  onUpdate: (updates: any) => void;
};

export const TemplateBuilderMediaStep: React.FC<TemplateBuilderMediaStepProps> = ({
  draft,
  onUpdate,
}) => {
  const theme = useTheme();
  const [imageUrl, setImageUrl] = useState(draft.coverImage || '');
  const [imageCredit, setImageCredit] = useState(draft.coverImageCredit || '');
  const [imageCreditUrl, setImageCreditUrl] = useState(draft.coverImageCreditUrl || '');

  const handleImageUrlChange = (url: string) => {
    setImageUrl(url);
    onUpdate({ coverImage: url || undefined });
  };

  const handleImageCreditChange = (credit: string) => {
    setImageCredit(credit);
    onUpdate({ coverImageCredit: credit || undefined });
  };

  const handleImageCreditUrlChange = (url: string) => {
    setImageCreditUrl(url);
    onUpdate({ coverImageCreditUrl: url || undefined });
  };

  const handlePickImage = async () => {
    try {
      // Request permissions
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Please grant permission to access your photo library to upload images. You can continue by pasting an image URL.',
          [
            { text: 'Use image URL', style: 'cancel' },
            { text: 'Open Settings', onPress: () => void Linking.openSettings() },
          ]
        );
        return;
      }

      // Pick image
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [16, 9],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        // For now, we'll use the local URI
        // In production, you'd upload this to Firebase Storage and get a URL
        handleImageUrlChange(asset.uri);
        Alert.alert(
          'Image Selected',
          'Image selected. Note: In production, this will be uploaded to cloud storage. For now, you can also paste an image URL directly.'
        );
      }
    } catch (error) {
      console.error('[TemplateBuilderMediaStep] Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again or use an image URL.');
    }
  };

  const handleRemoveImage = () => {
    setImageUrl('');
    setImageCredit('');
    setImageCreditUrl('');
    onUpdate({
      coverImage: undefined,
      coverImageCredit: undefined,
      coverImageCreditUrl: undefined,
    });
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      <Card style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Cover Image (Optional)</ThemedText>
        <Caption color={theme.colors.textSecondary} style={styles.sectionDescription}>
          Add a cover image to make your template more attractive. You can upload from your device or paste an image URL.
        </Caption>

        {/* Image Preview */}
        {imageUrl ? (
          <View style={styles.imagePreviewContainer}>
            <Image
              source={{ uri: imageUrl }}
              style={styles.imagePreview}
              resizeMode="cover"
            />
            <Pressable
              onPress={handleRemoveImage}
              style={[styles.removeImageButton, { backgroundColor: theme.colors.error }]}
            >
              <Ionicons name="trash-outline" size={20} color={theme.colors.surface} />
            </Pressable>
          </View>
        ) : (
          <View style={[styles.imagePlaceholder, { backgroundColor: theme.colors.background }]}>
            <Ionicons name="image-outline" size={48} color={theme.colors.textSecondary} />
            <Caption color={theme.colors.textSecondary} style={styles.placeholderText}>
              No cover image
            </Caption>
          </View>
        )}

        {/* Image Input Options */}
        <View style={styles.imageInputSection}>
          <InputField
            label="Image URL"
            placeholder="https://example.com/image.jpg"
            value={imageUrl}
            onChangeText={handleImageUrlChange}
            helperText="Paste an image URL or upload from device"
            style={styles.field}
          />

          {Platform.OS !== 'web' && (
            <Button
              label="Upload from Device"
              variant="secondary"
              icon="image-outline"
              onPress={handlePickImage}
              style={styles.uploadButton}
            />
          )}

          {Platform.OS === 'web' && (
            <View style={[styles.webUploadHint, { backgroundColor: theme.colors.background }]}>
              <Ionicons name="information-circle-outline" size={20} color={theme.colors.primary} />
              <Caption color={theme.colors.textSecondary} style={styles.hintText}>
                On web, paste an image URL. For device uploads, use the mobile app.
              </Caption>
            </View>
          )}
        </View>

        {/* Image Credit */}
        {imageUrl && (
          <View style={styles.creditSection}>
            <ThemedText style={styles.creditTitle}>Image Credit (Optional)</ThemedText>
            <Caption color={theme.colors.textSecondary} style={styles.creditDescription}>
              Give credit to the image source if required
            </Caption>
            <InputField
              label="Credit Text"
              placeholder="e.g., Photo by John Doe"
              value={imageCredit}
              onChangeText={handleImageCreditChange}
              style={styles.field}
            />
            <InputField
              label="Credit URL"
              placeholder="https://example.com/photo"
              value={imageCreditUrl}
              onChangeText={handleImageCreditUrlChange}
              style={styles.field}
            />
          </View>
        )}

        {/* Tips */}
        <View style={[styles.tipsCard, { backgroundColor: theme.colors.background }]}>
          <View style={styles.tipsHeader}>
            <Ionicons name="bulb-outline" size={20} color={theme.colors.primary} />
            <ThemedText style={styles.tipsTitle}>Image Tips</ThemedText>
          </View>
          <View style={styles.tipsList}>
            <Caption color={theme.colors.textSecondary}>
              • Use high-quality images (at least 1200x675px)
            </Caption>
            <Caption color={theme.colors.textSecondary}>
              • Recommended aspect ratio: 16:9
            </Caption>
            <Caption color={theme.colors.textSecondary}>
              • Use images that represent the destination well
            </Caption>
            <Caption color={theme.colors.textSecondary}>
              • Ensure you have rights to use the image
            </Caption>
          </View>
        </View>
      </Card>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 16,
  },
  section: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  sectionDescription: {
    marginBottom: 16,
  },
  imagePreviewContainer: {
    position: 'relative',
    marginBottom: 16,
    borderRadius: 12,
    overflow: 'hidden',
  },
  imagePreview: {
    width: '100%',
    height: 200,
  },
  removeImageButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePlaceholder: {
    width: '100%',
    height: 200,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#ddd',
  },
  placeholderText: {
    marginTop: 8,
  },
  imageInputSection: {
    gap: 12,
    marginBottom: 16,
  },
  field: {
    marginBottom: 12,
  },
  uploadButton: {
    marginTop: 8,
  },
  webUploadHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  hintText: {
    flex: 1,
    fontSize: 12,
  },
  creditSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  creditTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  creditDescription: {
    marginBottom: 12,
  },
  tipsCard: {
    padding: 16,
    borderRadius: 8,
    marginTop: 16,
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  tipsTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  tipsList: {
    gap: 6,
  },
});
